import flattenDeep from 'lodash/flattenDeep';
import React from 'react';
import classNames from 'classnames';

import ErrorHandler from '@module/error-handler';
import Analytics from '@module/analytics';
import I18n from '@module/i18n';

import { TransferItemAPITypes } from '@app/transfer-bundle/constant/transfer-item-types';

// So, this might not be the best way to do this - but
// it does work. So `Dropzone` is both a static class
// and React component in one.
export default class Dropzone extends React.Component {
  static mounted = false;
  static target = false;

  // The state decides whether or not the dropzone is really visible
  state = { visible: false, animated: false };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.onDragOver = this.onDragOver.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
  }

  // Set the current uploader target, this will enable the
  // Dropzone so the user can easily drop the files.
  static setTarget(target, uploader) {
    if (!target || typeof target.addFiles !== 'function') {
      throw 'InvalidDropTarget';
    }

    Dropzone.target = target;

    if (!uploader) {
      return;
    }
    // Also enable dropping files on our uploader component
    uploader.addEventListener('drop', this.mounted.onDrop, false);
  }

  // Remove the current uploader target, this will disable
  // the Dropzone component from working.
  static removeTarget(uploader) {
    Dropzone.target = null;

    // Also disable the drop event on our uploader component
    if (!uploader) {
      return;
    }
    uploader.removeEventListener('drop', this.mounted.onDrop, false);
  }

  // Try to show the dropzone
  static show() {
    if (!Dropzone.mounted) {
      return;
    }
    if (!Dropzone.target) {
      return false;
    }
    Dropzone.mounted.show();
  }

  // Try to hide the dropzone
  static hide() {
    if (!Dropzone.mounted) {
      return;
    }
    Dropzone.mounted.hide();
  }

  // Check if the drag event is supported by Dropzone, basically
  // we just have 1 place where we make sure the drag/drop cmd
  // actually contains files.
  static availableForEvent(e) {
    if (!Dropzone.target) {
      return false;
    }
    if (!e || !e.dataTransfer) {
      return false;
    }
    if (!e.dataTransfer.types) {
      return false;
    }

    try {
      // .types is not always an Array so does not always have .indexOf support
      const types = Array.prototype.slice.call(e.dataTransfer.types);
      return types.indexOf('Files') !== -1;
    } catch (e) {
      return false;
    }
  }

  // We are using the dragover event to determine whether someone is
  // dragging files in our window. In most cases the dragging will
  // actually be happening in the wallpaper iframe though.
  onDragOver(e) {
    e.preventDefault();
    if (!Dropzone.availableForEvent(e)) {
      return;
    }
    Dropzone.show();
  }

  // Leaving the dropzone area with our draggable object, so hide it again
  onDragLeave(e) {
    e.preventDefault();
    Dropzone.hide();
  }

  /**
   * Reader (which is FileSystemDirectoryReader) can read entries from a directory.
   * It reads not everything, but bunches of entries.
   * Chrome, for example, does it by 100 items.
   * To get all entries, consequent calls to the `.readEntries` must be made.
   * Each call will reveal an array of items.
   * Empty array means nothing more to read.
   * The same instance of a FileSystemDirectoryReader must be used for each
   * call to `readEntries`. Otherwise entries listing will start over.
   */
  getSubEntries = (reader) =>
    new Promise((resolve, reject) =>
      reader.readEntries(resolve, reject)
    ).then(async (subEntries) =>
      subEntries.length
        ? subEntries.concat(await this.getSubEntries(reader))
        : subEntries
    );

  getFileFromEntry = (entry) =>
    new Promise((resolve, reject) => entry.file(resolve, reject));

  /**
   * The whole reason for this function is to put comments here
   * instead of bloating {@link this#getFilesFromEntries}
   *
   * One of the noticeable changes in adding directory support
   * is switching from just file names to file paths.
   * Server responds with relative "path/to/file"
   * and expects the same during upload.
   * Here `file` argument is an instance of `File` class.
   * It has read-only `name` field, making it impossible to change it's value.
   * Thus the new `relativePath` field is added.
   * `File.webkitRelativePath` could be a candidate for that.
   * Unfortunately not in this case.
   * Here `file` is extracted out of `entry` but not from the `input` element,
   * thus `webkitRelativePath` is empty.
   * That's why `entry.fullPath` is used.
   * To transform it from "absolute" form to "relative"
   * first character (which is forward slash) is sliced out.
   */
  addRelativePathToFile = (file, entry) => {
    file.relativePath = entry.fullPath.slice(1);
  };

  /**
   * empty directory does not need to contain anything
   * even the `name` is taken from the `relativePath`
   * which will be added later
   */
  getEmptyDirectory = () => ({});

  getFilesFromEntries(entries) {
    return Promise.all(
      entries.map(async (entry) => {
        let file;

        if (entry.isDirectory) {
          const subEntries = await this.getSubEntries(entry.createReader());

          if (subEntries.length) {
            return this.getFilesFromEntries(subEntries);
          }

          file = this.getEmptyDirectory();
          file.apiType = TransferItemAPITypes.emptyDirectory;
        } else {
          file = await this.getFileFromEntry(entry);
          file.apiType = TransferItemAPITypes.file;
        }

        this.addRelativePathToFile(file, entry);

        return file;
      })
    ).then(flattenDeep);
  }

  // When dropping a file we'll first want to make sure it contains
  // actual files otherwise it's not for us to handle.
  onDrop = async (e) => {
    // This prevents browser's default behaviour to render dropped item itself
    e.preventDefault();

    // If this is a supported event and we have an uploader, let's DO THIS!
    if (Dropzone.target) {
      let files = e.dataTransfer.files;

      // some (old) browsers don't support it
      // it's better to check it earlier
      if (e.dataTransfer.items) {
        try {
          // convert ItemList to Array
          const itemsArray = Array.from(e.dataTransfer.items);
          const entries = itemsArray.map((item) => item.webkitGetAsEntry());
          files = await this.getFilesFromEntries(entries);
        } catch (error) {
          ErrorHandler.track(error);
        }
      }

      Analytics.trackGoogleEvent({
        category: 'Transfer',
        action: 'File Added',
        label: 'dropzone',
      });

      Dropzone.target.addFiles(files);
    }

    Dropzone.hide();
  };

  // When the component mounts we need to do some things
  componentDidMount() {
    if (!this.node) {
      return;
    }
    // Store the reference to this component
    Dropzone.mounted = this;

    // Bind our events
    document.body.addEventListener('dragover', this.onDragOver, false);
    this.node.addEventListener('dragleave', this.onDragLeave, false);
    this.node.addEventListener('drop', this.onDrop, false);
  }

  // Show the dropzone
  show() {
    this.setState({
      visible: true,
      animated: false,
    });
  }

  // Hide the dropzone
  hide(animated = false) {
    this.setState({ visible: false, animated });
  }

  // When a new state is set we might not need to re-render,
  // given the state can be identical to the current one.
  shouldComponentUpdate(nextProps, nextState) {
    return nextState.visible !== this.state.visible;
  }

  // Cleanup the component
  componentWillUnmount() {
    if (!this.node) {
      return;
    }

    Dropzone.mounted = false;

    // Remove global event listener
    document.body.removeEventListener('dragover', this.onDragOver, false);
    // Remove the dropzone event listeners too
    this.node.removeEventListener('dragleave', this.onDragLeave, false);
    this.node.removeEventListener('drop', this.onDrop, false);
  }

  // This is a simple representation of our dropzone
  render() {
    const dropzoneClassnames = classNames('dropzone', {
      'dropzone--visible': this.state.visible,
    });

    return (
      <div ref={(node) => (this.node = node)} className={dropzoneClassnames}>
        <h3 className="dropzone__title">{I18n.t('dropzone.title')}</h3>
        <p className="dropzone__text">{I18n.t('dropzone.text')}</p>
      </div>
    );
  }
}
